import {List, ListItem, Typography, Box} from '@mui/material'
import {
  format,
  isToday,
  isThisYear,
  isWithinInterval,
  subDays,
  getYear
} from 'date-fns'
import SessionItem from './SessionItem'
import {useTranslation} from 'react-i18next'

export type SessionItemData = {
  id: string
  lastMessageDateCreated: number
  topic?: string
  demandId?: string
}

interface SessionsProps {
  sessions: SessionItemData[]
  selectedSession: SessionItemData
  onSelectSession: (item: SessionItemData) => void
  onDeleteSession?: (item: SessionItemData) => void
}

const Sessions = ({
  sessions,
  selectedSession,
  onSelectSession,
  onDeleteSession
}: SessionsProps) => {
  const [t] = useTranslation()

  const TODAY = t('fnd-assistant-sessions-menu-today')
  const LAST_7_DAYS = t('fnd-assistant-sessions-menu-7-days')
  const LAST_30_DAYS = t('fnd-assistant-sessions-menu-30-days')

  const groupSessions = (items: SessionItemData[]) => {
    const now = new Date()
    const groups: Record<string, SessionItemData[]> = {}

    items.forEach((item) => {
      const date = new Date(item.lastMessageDateCreated)

      if (isToday(date)) {
        groups[TODAY] = groups[TODAY] || []
        groups[TODAY].push(item)
      } else if (isWithinInterval(date, {start: subDays(now, 7), end: now})) {
        groups[LAST_7_DAYS] = groups[LAST_7_DAYS] || []
        groups[LAST_7_DAYS].push(item)
      } else if (isWithinInterval(date, {start: subDays(now, 30), end: now})) {
        groups[LAST_30_DAYS] = groups[LAST_30_DAYS] || []
        groups[LAST_30_DAYS].push(item)
      } else if (isThisYear(date)) {
        const month = format(date, 'MMMM')
        groups[month] = groups[month] || []
        groups[month].push(item)
      } else {
        const year = getYear(date).toString()
        groups[year] = groups[year] || []
        groups[year].push(item)
      }
    })

    const sortSessionGroup = (label: string) => {
      if (groups[label]) {
        groups[label].sort(
          (a, b) => b.lastMessageDateCreated - a.lastMessageDateCreated
        )
      }
    }

    sortSessionGroup(TODAY)
    sortSessionGroup(LAST_7_DAYS)
    sortSessionGroup(LAST_30_DAYS)

    return groups
  }

  const groupedSessions = groupSessions(sessions)
  const fixedLabels = [TODAY, LAST_7_DAYS, LAST_30_DAYS]

  const monthLabels = Array.from({length: 12}, (_, i) =>
    format(new Date(new Date().getFullYear(), i, 1), 'MMMM')
  ).reverse()

  const yearLabels = Object.keys(groupedSessions)
    .filter((label) => /^\d{4}$/.test(label))
    .sort((a, b) => Number(b) - Number(a))

  const orderedLabels = [
    ...fixedLabels,
    ...monthLabels.filter((m) => groupedSessions[m]),
    ...yearLabels
  ]

  return (
    <Box paddingLeft="15px">
      {orderedLabels.map(
        (label) =>
          groupedSessions[label] && (
            <Box key={label} paddingY={1}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                {label}
              </Typography>
              <List>
                {groupedSessions[label].map((session) => (
                  <ListItem key={session.id} sx={{padding: 0}}>
                    <SessionItem
                      session={session}
                      selected={selectedSession?.id === session.id}
                      onSelectSession={(session) => onSelectSession(session)}
                      onDeleteSession={(session) =>
                        onDeleteSession && onDeleteSession(session)
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )
      )}
    </Box>
  )
}

export default Sessions
